import { formatDistanceToNow, isPast } from "date-fns";
import { fr } from "date-fns/locale";

/**
 * Calcule le temps restant avant expiration d'un témoignage
 * @param expires_at - Date d'expiration ISO string
 * @returns Texte formaté du temps restant ou "Expiré"
 */
export const getTimeUntilExpiration = (expires_at: string): string => {
  const expirationDate = new Date(expires_at);

  if (isPast(expirationDate)) {
    return "Expiré";
  }

  return formatDistanceToNow(expirationDate, {
    addSuffix: true,
    locale: fr,
  });
};

/**
 * Tronque un texte à un nombre de lignes spécifié
 * @param content - Contenu à tronquer
 * @param maxLines - Nombre maximum de lignes (défaut: 3)
 * @returns Texte tronqué avec "..." si nécessaire
 */
export const truncateContent = (
  content: string,
  maxLines: number = 3
): string => {
  if (!content) return "";

  const lines = content.split("\n");

  if (lines.length <= maxLines) {
    return content;
  }

  return lines.slice(0, maxLines).join("\n") + "...";
};

/**
 * Message par défaut pour une demande de témoignage
 * Note: Cette fonction sera remplacée par une traduction i18n dans les composants
 * @param friendName - Prénom de l'ami
 * @param chapterTitle - Titre du chapitre
 * @param t - Fonction de traduction i18n
 * @returns Message personnalisé par défaut
 */
export const getDefaultAnecdoteMessage = (
  friendName: string,
  chapterTitle: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  return t("anecdotes.defaultMessage", { friendName, chapterTitle });
};

/**
 * Retourne la couleur du badge en fonction du statut
 * @param status - Statut du témoignage
 * @returns Classes Tailwind pour le badge
 */
export const getStatusColor = (
  status: string
): { bg: string; text: string } => {
  switch (status) {
    case "pending":
      return {
        bg: "bg-sand-50",
        text: "text-sand-700",
      };
    case "submitted":
      return {
        bg: "bg-sand-50",
        text: "text-sand-700",
      };
    case "accepted":
      return {
        bg: "bg-herb-300",
        text: "text-herb-800",
      };
    case "rejected":
      return {
        bg: "bg-rust-10",
        text: "text-rust-700",
      };
    case "expired":
      return {
        bg: "bg-slate-50",
        text: "text-slate-600",
      };
    default:
      return {
        bg: "bg-slate-50",
        text: "text-slate-600",
      };
  }
};
